import { useState } from "react";
import { toast } from "sonner";
import { Car, Bike, Home as HomeIcon, Heart, Check } from "lucide-react";
import { api, formatApiError } from "../lib/api";

const types = [
  { key: "auto", icon: Car, label: "Auto" },
  { key: "moto", icon: Bike, label: "Moto" },
  { key: "casa", icon: HomeIcon, label: "Casa" },
  { key: "vita", icon: Heart, label: "Vita" },
];

export default function Assicurazioni() {
  const [form, setForm] = useState({ type: "auto", name: "", email: "", phone: "", details: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/quotes", form);
      setDone(true);
      toast.success("Richiesta di preventivo inviata!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore durante l'invio.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="assicurazioni-page">
      <section className="bg-white py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#F97316]">Assicurazioni</span>
          <h1 className="mt-3 max-w-3xl font-display text-4xl font-extrabold tracking-tighter text-[#0A192F] sm:text-5xl">
            Preventivi assicurativi gratuiti
          </h1>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-slate-600">
            Richiedi un preventivo gratuito e senza impegno. Il servizio è disponibile per tutti,
            anche senza abbonamento al Piano ViviTranquillo.
          </p>
        </div>
      </section>

      <section className="bg-[#F8FAFC] py-16 lg:py-24">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-2 lg:px-8">
          <div>
            <h2 className="font-display text-2xl font-bold text-[#0A192F]">Cosa possiamo assicurare</h2>
            <div className="mt-6 grid grid-cols-2 gap-4">
              {types.map((t) => (
                <div key={t.key} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-5">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#F97316]/10">
                    <t.icon className="h-5 w-5 text-[#F97316]" />
                  </span>
                  <span className="font-display font-bold text-[#0A192F]">{t.label}</span>
                </div>
              ))}
            </div>
            <div className="mt-6 space-y-3">
              {["Preventivo 100% gratuito", "Nessun obbligo di abbonamento", "Risposta rapida da parte del team"].map((f) => (
                <p key={f} className="flex items-center gap-3 text-slate-700">
                  <Check className="h-5 w-5 text-[#10B981]" /> {f}
                </p>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            {done ? (
              <div data-testid="quote-success" className="flex flex-col items-center justify-center py-10 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#10B981]/10">
                  <Check className="h-7 w-7 text-[#10B981]" />
                </span>
                <h3 className="mt-5 font-display text-xl font-bold text-[#0A192F]">Richiesta inviata!</h3>
                <p className="mt-2 text-slate-600">Ti contatteremo al più presto con il tuo preventivo gratuito.</p>
                <button data-testid="quote-reset" onClick={() => { setDone(false); setForm({ type: "auto", name: "", email: "", phone: "", details: "" }); }} className="mt-6 rounded-full border-2 border-slate-200 px-6 py-2.5 font-semibold text-[#0A192F] transition-colors hover:border-[#0A192F]">
                  Nuovo preventivo
                </button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="quote-form" className="space-y-5">
                <h3 className="font-display text-xl font-bold text-[#0A192F]">Richiedi il tuo preventivo</h3>
                <div>
                  <label className="mb-2 block text-sm font-semibold text-[#0A192F]">Tipo di assicurazione</label>
                  <div className="grid grid-cols-4 gap-2">
                    {types.map((t) => (
                      <button
                        key={t.key}
                        type="button"
                        data-testid={`quote-type-${t.key}`}
                        onClick={() => setForm({ ...form, type: t.key })}
                        className={`flex flex-col items-center gap-1.5 rounded-xl border-2 py-3 text-xs font-semibold transition-colors ${
                          form.type === t.key ? "border-[#10B981] bg-[#10B981]/5 text-[#0A192F]" : "border-slate-200 text-slate-500"
                        }`}
                      >
                        <t.icon className="h-5 w-5" /> {t.label}
                      </button>
                    ))}
                  </div>
                </div>
                <input data-testid="quote-name" required placeholder="Nome e cognome" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#10B981]" />
                <input data-testid="quote-email" required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#10B981]" />
                <input data-testid="quote-phone" placeholder="Telefono (facoltativo)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#10B981]" />
                <textarea data-testid="quote-details" placeholder="Dettagli della richiesta" rows={3} value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#10B981]" />
                <button data-testid="quote-submit" disabled={loading} className="w-full rounded-full bg-[#10B981] px-8 py-3.5 font-semibold text-white transition-colors hover:bg-[#059669] disabled:opacity-60">
                  {loading ? "Invio in corso..." : "Richiedi preventivo gratuito"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
